// src/pages/produtos/produtos/ProdutoSearch.js
import React, { useEffect, useState, useRef } from 'react';
import { useProduto } from '../../../contexts/produtos/ProdutoContext';
import { FaSortUp, FaSortDown } from "react-icons/fa";

import { formatNumeric } from '../../../utils/numberUtils';
import LazyLoading from '../../../components/modalForm/LazyLoading';
import ModalTable from '../../../components/modalForm/ModalTable';

import './ProdutoSearch.css';

// Função para remover acentos
// Normalização centralizada para manter as pesquisas de produto consistentes.
import { normalizarTextoProduto, produtoCorrespondeFiltro } from '../../../services/produtos/ProdutoFiltroService';
import ModalContainer from '../../../components/modalForm/ModalContainer';
import ModalHeader from '../../../components/modalForm/ModalHeader';
import ModalBody from '../../../components/modalForm/ModalBody';
import ModalFooter from '../../../components/modalForm/ModalFooter';
import ModalSearchBar from '../../../components/modalForm/ModalSearchBar';
import { ModalActionButton } from '../../../components/modalForm/ModalActions';
import ModalSelectButton from '../../../components/modalForm/ModalSelectButton';
import InputCheck from '../../../components/input/InputCheck';

const ProdutoSearch = ({ show, onHide, onSelectProduto }) => {
    const { produtos } = useProduto();
    const [filter, setFilter] = useState('');
    const [listarInativos, setListarInativos] = useState(false); // ✅ novo estado
    const [sortField, setSortField] = useState('id'); 
    const [sortDirection, setSortDirection] = useState('asc');

    const inputRef = useRef(null);

    useEffect(() => {
        if (show) {
            setFilter('');
            setListarInativos(false); // ✅ resetar sempre que abrir
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }
    }, [show]);

    if (!show) return null;

    // ✅ Filtra os produtos ignorando acentos e considerando ativos/inativos
    const filteredProdutos = produtos.filter(produto =>
        produtoCorrespondeFiltro(produto?.descricao, filter) &&
        (listarInativos || (!produto.grupo?.inativo && !produto.inativo))
    );

    // Ordena os produtos
    const sortedProdutos = filteredProdutos.sort((a, b) => {
        if (sortField === 'id') {
            return sortDirection === 'asc' ? a.id - b.id : b.id - a.id;
        } else if (sortField === 'descricao') {
            const descriptionA = normalizarTextoProduto(a.descricao);
            const descriptionB = normalizarTextoProduto(b.descricao);
            if (descriptionA < descriptionB) return sortDirection === 'asc' ? -1 : 1;
            if (descriptionA > descriptionB) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        }
        return 0;
    });

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleDoubleClick = (produto) => {
        onSelectProduto(produto);
        onHide();
    };

    return (
        <ModalContainer show={show} largura="800px" contentClassName="produto-search-modal" variant="busca" onHide={onHide}>

            <ModalHeader title="Buscar Produto" onHide={onHide} />
            <ModalBody className="modal-body" style={{ marginBottom: '0px', paddingBottom: '0px' }}>
                <div className="mb-3 row">
                    <div className="col-md-9">
                        <ModalSearchBar
                            inputRef={inputRef}
                            type="text"

                            placeholder="Filtrar por descrição..."
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        />
                    </div>
                    <div className="col-md-3 d-flex align-items-center">
                        <InputCheck id="listarInativos" label="Inativos" checked={listarInativos}
                            onChange={(e) => setListarInativos(e.target.checked)} />
                    </div>
                </div>

                <LazyLoading
                    items={sortedProdutos}
                    resetKey={`${filter}|${listarInativos}|${sortField}|${sortDirection}`}
                >
                {({ containerRef, handleScroll, visibleItems }) => (
                <ModalTable
                    containerRef={containerRef}
                    containerStyle={{ maxHeight: '55vh', overflowY: 'auto' }}
                    onContainerScroll={handleScroll}
                >
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr>
                            <th className="text-center align-middle produto-search-id" scope="col" aria-sort={sortField === 'id' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                <button
                                    onClick={() => handleSort('id')}
                                    style={{ fontWeight: 'bold', background: 'none', border: 'none' }}
                                    tabIndex={0}
                                 type="button">
                                    Id
                                    {sortField === 'id' && (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />)}
                                </button>
                            </th>
                            <th className="align-middle produto-search-descricao" scope="col" aria-sort={sortField === 'descricao' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                <button
                                    onClick={() => handleSort('descricao')}
                                    style={{ fontWeight: 'bold', background: 'none', border: 'none' }}
                                    tabIndex={0}
                                 type="button">
                                    Descrição
                                    {sortField === 'descricao' && (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />)}
                                </button>
                            </th>
                            <th className="align-middle produto-search-grupo" scope="col" tabIndex="-1">
                                Grupo
                            </th>
                            <th className="text-end align-middle produto-search-preco" scope="col" tabIndex="-1">
                                Preço
                            </th>
                            <th className="text-center align-middle produto-search-selecionar modal-select-column" scope="col" tabIndex="-1">
                                Selecionar
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {visibleItems.map((produto) => (
                            <tr
                                key={produto.id}
                                onDoubleClick={() => handleDoubleClick(produto)}
                            >
                                <td className="text-end align-middle produto-search-id">{produto.id}</td>
                                <td className="text-start align-middle produto-search-descricao">{produto.descricao}</td>
                                <td className="text-start align-middle produto-search-grupo">{produto.grupo?.descricao}</td>
                                <td className="text-end align-middle produto-search-preco">{formatNumeric(produto.preco)}</td>
                                <td className="text-center align-middle produto-search-selecionar modal-select-column">
                                    <ModalSelectButton
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm produto-search-botao-selecionar"
                                        style={{ padding: '2px 6px' }}
                                        onClick={() => onSelectProduto(produto)}
                                        aria-label={`Selecionar ${produto.descricao}`}
                                     />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </ModalTable>
                )}
                </LazyLoading>
            </ModalBody>
            <ModalFooter className="modal-footer">
                <ModalActionButton className="btn btn-secondary btn-sm" onClick={onHide} action="close">
                    Fechar
                </ModalActionButton>
            </ModalFooter>

        </ModalContainer>
    );
};

export default ProdutoSearch;
